import { createMemo, createSignal, For, Show } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { ChevronRight, Dumbbell, Flame, ListChecks, Play, Plus } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import {
  type RoutineWithCount,
  startOfWeek,
  startSessionMutation,
  totalVolume,
} from "@gymlog/shared";
import {
  useOpenSessionQuery,
  useRoutinesQuery,
  useSessionsQuery,
  useSetsSinceQuery,
} from "@gymlog/shared/solid";

import { Card, CardContent, PageContainer } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useGymCtx } from "../hooks/use-gym-ctx";
import { formatKg } from "../lib/labels";
import { CardListSkeleton } from "./gym-skeletons";

const RECENT_SESSIONS_LIMIT = 30;
const RECENT_SESSIONS_SHOWN = 3;

/** Main screen: start/continue workout session. */
export function GymHome() {
  const ctx = useGymCtx();
  const { t } = ctx;
  const navigate = useNavigate();
  const [starting, setStarting] = createSignal(false);

  const baseOptions = {
    db: ctx.db,
    drizzle: ctx.drizzle,
    userId: ctx.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  };

  const openSession = useOpenSessionQuery(baseOptions);
  const routines = useRoutinesQuery(baseOptions);
  const sessions = useSessionsQuery({
    ...baseOptions,
    limit: () => RECENT_SESSIONS_LIMIT,
  });

  const weekStartISO = createMemo(() => startOfWeek(new Date()).toISOString());
  const weekSets = useSetsSinceQuery({
    ...baseOptions,
    sinceISO: weekStartISO,
  });

  const weekStats = createMemo(() => {
    const rows = weekSets.data ?? [];
    const sessionIds = new Set(rows.map((r) => r.session_id).filter(Boolean));
    return { volume: totalVolume(rows), sessionCount: sessionIds.size };
  });

  const finishedSessions = createMemo(() =>
    (sessions.data ?? []).filter((s) => s.finished_at).slice(0, RECENT_SESSIONS_SHOWN),
  );

  const startWorkout = async (routine?: RoutineWithCount) => {
    const db = ctx.drizzle();
    const uid = ctx.userId();
    if (!db || !uid || starting()) {
      return;
    }
    // If an open session already exists, continue that one instead of opening another.
    const open = openSession.data;
    if (open) {
      void navigate({
        to: "/session/$sessionId",
        params: { sessionId: open.id },
      });
      return;
    }
    setStarting(true);
    try {
      const id = await startSessionMutation(db, uid, {
        name: routine?.name ?? t(msg`Free workout`),
        routineId: routine?.id ?? null,
      });
      void navigate({ to: "/session/$sessionId", params: { sessionId: id } });
    } finally {
      setStarting(false);
    }
  };

  const formatDay = (iso: string | null) => {
    if (!iso) {
      return "";
    }
    return new Date(iso).toLocaleDateString(ctx.locale(), {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  return (
    <div class="flex-1 overflow-y-auto">
      <PageContainer maxWidth="3xl" class="py-6 space-y-6">
        <div class="select-none">
          <h1 class="text-3xl font-extrabold tracking-tight flex items-center gap-2">
            <Dumbbell class="h-8 w-8 text-primary shrink-0" />
            {t(msg`Train`)}
          </h1>
          <p class="text-sm text-muted-foreground mt-1">
            {t(msg`{count} workouts this week · {volume} kg lifted`, {
              count: weekStats().sessionCount,
              volume: formatKg(weekStats().volume),
            })}
          </p>
        </div>

        {/* Open session → continue */}
        <Show when={openSession.data}>
          {(open) => (
            <Card class="border-primary/40 bg-primary/5">
              <CardContent class="p-4 flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-xs uppercase tracking-wider text-primary font-bold flex items-center gap-1.5">
                    <Flame class="h-3.5 w-3.5" />
                    {t(msg`Workout in progress`)}
                  </p>
                  <p class="text-lg font-semibold truncate">{open().name}</p>
                </div>
                <Button
                  class="gap-2 shrink-0"
                  data-testid="continue-session"
                  onClick={() =>
                    void navigate({
                      to: "/session/$sessionId",
                      params: { sessionId: open().id },
                    })
                  }
                >
                  <Play class="h-4 w-4" />
                  {t(msg`Continue`)}
                </Button>
              </CardContent>
            </Card>
          )}
        </Show>

        {/* Routines to start */}
        <section class="space-y-2">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              {t(msg`Your routines`)}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              class="gap-1.5"
              onClick={() => void navigate({ to: "/routines" })}
            >
              {t(msg`Manage`)}
              <ChevronRight class="h-4 w-4" />
            </Button>
          </div>

          <Show when={!routines.isPending} fallback={<CardListSkeleton rows={3} />}>
            <Show
              when={(routines.data ?? []).length > 0}
              fallback={
                <EmptyState
                  icon={ListChecks}
                  title={t(msg`No routines yet`)}
                  description={t(msg`Create a routine (e.g. Push, Pull, Legs) to start faster.`)}
                  class="py-4"
                />
              }
            >
              <div class="grid gap-2">
                <For each={routines.data}>
                  {(routine) => (
                    <Card class="hover:border-primary/30 transition-colors">
                      <CardContent class="p-4 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          class="min-w-0 text-left cursor-pointer flex-1"
                          onClick={() =>
                            void navigate({
                              to: "/routines/$routineId",
                              params: { routineId: routine.id },
                            })
                          }
                        >
                          <p class="font-semibold truncate">{routine.name}</p>
                          <p class="text-xs text-muted-foreground">
                            {t(msg`{count} exercises`, {
                              count: routine.exercise_count,
                            })}
                          </p>
                        </button>
                        <Button
                          variant="outline"
                          size="sm"
                          class="gap-1.5 shrink-0"
                          disabled={starting() || !ctx.drizzle() || !ctx.userId()}
                          data-testid={`start-routine-${routine.name}`}
                          onClick={() => void startWorkout(routine)}
                        >
                          <Play class="h-3.5 w-3.5" />
                          {t(msg`Start`)}
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </div>
            </Show>
          </Show>

          <Button
            variant="secondary"
            class="w-full gap-2"
            disabled={starting() || !ctx.drizzle() || !ctx.userId()}
            data-testid="start-free-workout"
            onClick={() => void startWorkout()}
          >
            <Plus class="h-4 w-4" />
            {t(msg`Start empty workout`)}
          </Button>
        </section>

        {/* Recent sessions */}
        <Show when={finishedSessions().length > 0}>
          <section class="space-y-2">
            <div class="flex items-center justify-between">
              <h2 class="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                {t(msg`Recent workouts`)}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                class="gap-1.5"
                onClick={() => void navigate({ to: "/history" })}
              >
                {t(msg`History`)}
                <ChevronRight class="h-4 w-4" />
              </Button>
            </div>
            <div class="stagger-children grid gap-2">
              <For each={finishedSessions()}>
                {(s) => (
                  <button
                    type="button"
                    class="w-full text-left cursor-pointer rounded-xl transition-transform duration-150 ease-swift will-change-transform active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    onClick={() =>
                      void navigate({
                        to: "/session/$sessionId",
                        params: { sessionId: s.id },
                      })
                    }
                  >
                    <Card class="hover:border-primary/30 transition-colors">
                      <CardContent class="p-3.5 flex items-center justify-between gap-3">
                        <div class="min-w-0">
                          <p class="font-medium truncate">{s.name}</p>
                          <p class="text-xs text-muted-foreground">{formatDay(s.started_at)}</p>
                        </div>
                        <div class="text-right shrink-0">
                          <p
                            class="text-base tabular-nums tracking-tight leading-none"
                            style={{
                              "font-family": "var(--font-display)",
                              "font-weight": "600",
                            }}
                          >
                            {formatKg(s.volume_kg)} kg
                          </p>
                          <p class="text-xs text-muted-foreground">
                            {t(msg`{count} sets`, { count: s.set_count })}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                )}
              </For>
            </div>
          </section>
        </Show>
      </PageContainer>
    </div>
  );
}
